import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiRequestError } from '@/api/errors';

interface ApiResourceState<T> {
  data: T | undefined;
  loading: boolean;
  error: ApiRequestError | undefined;
  reload: () => void;
}

const genericErrorMessage = 'Servicio no disponible temporalmente. Inténtelo de nuevo más tarde.';

/**
 * Fetches one API resource on mount and whenever `watchKey` changes,
 * tracking loading/error state for the shell's designed states
 * (`LoadingState`, `ErrorState`, `AccessDenied`). Discards a response that
 * resolves after a newer request started, so a fast filter change never
 * lets a stale response overwrite the current one.
 *
 * `watchKey` is a caller-computed string (typically `JSON.stringify` of the
 * active filters) rather than a dependency array, since the filter shape
 * varies per screen and this hook is shared across all of them.
 */
export function useApiResource<T>(fetcher: () => Promise<T>, watchKey: string): ApiResourceState<T> {
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiRequestError>();
  const requestId = useRef(0);
  const [reloadToken, setReloadToken] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    const currentRequest = ++requestId.current;
    setLoading(true);
    setError(undefined);

    fetcherRef
      .current()
      .then((result) => {
        if (requestId.current !== currentRequest) return;
        setData(result);
        setLoading(false);
      })
      .catch((caught: unknown) => {
        if (requestId.current !== currentRequest) return;
        setError(caught instanceof ApiRequestError ? caught : new ApiRequestError(0, 'unknown', genericErrorMessage));
        setLoading(false);
      });
  }, [watchKey, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { data, loading, error, reload };
}
